import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { Badge } from "@ds/ui/badge";
import { EmptyState } from "@ds/patterns/empty-state";
import { Skeleton } from "@ds/ui/skeleton";
import { groupedWeight } from "@/app/lib/number";
import { useUnits } from "@/app/lib/units";
import { haptics } from "@/app/platform";
import { printedMax } from "../movements.chart";
import { civilDate } from "../movements.converter";
import { useMovementLibrary } from "../movements.queries";
import { canDrawSparkline, standingSeries } from "../movements.standing";
import { MovementSparkline } from "./MovementSparkline";
import type { MovementSummaryModel } from "../movements.model";

/**
 * EVERY MOVEMENT THIS ATHLETE HAS EVER PERFORMED, most recently trained first.
 *
 * ═══ 🚨 WHAT A ROW SAYS, AND WHAT IT USED TO SAY ═══
 *
 * It used to say this:
 *
 *     KB swing
 *     Last 2026-09-05 · 25 sessions · 81'720 kg
 *
 * Four facts, in a list two hundred rows long, none of them ranked — an ISO date nobody reads
 * as a date, a session count nobody is deciding anything with, and a six-digit tonnage
 * competing with the name for the same glance. That is a database row printed for a developer.
 * At forty movements it is eighty lines of digits, and the athlete's eye has nowhere to land.
 *
 * A row now says a NAME and ONE NUMBER:
 *
 *     Back squat                                                                    118 kg
 *     Pull-up                                                                  3 weeks ago
 *
 * ═══ WHY THAT NUMBER AND NOT ANOTHER ═══
 *
 * 🔑 THE ATHLETE'S MAX IS THE ONLY FIGURE HERE THAT ANSWERS A QUESTION. "Am I stronger at this
 * than I was?" — and it is comparable between two rows and between two months, which a volume
 * and a session count are not. Everything else that used to be on the row lives one tap away on
 * the screen that can put it in context; nothing was deleted, it was RANKED.
 *
 * 🚨 AND IT IS THE ENGINE'S NUMBER (BUD-375) — the same one the movement screen leads with and
 * the same one the next session is priced off, resolved once on the service. The row does not
 * say where it came from; the screen it opens does, with a badge.
 *
 * ⚠️ AND FOR A MOVEMENT WITH NO WEIGHT ON IT THE ANSWER IS WHEN, NOT HOW MUCH. A pull-up has no
 * estimate, and a rep total answers nothing — 4'120 reps of pull-ups over three years is not a
 * fact anybody acts on. What an athlete wants from an unloaded movement in a list is whether
 * they have neglected it, so the row says "3 weeks ago".
 *
 * ═══ IT FILTERS ON THE PHONE, WHICH IS A DELIBERATE DEPARTURE ═══
 *
 * Every other list in this product is keyset-paginated over the wire, because every other list
 * grows without bound. This one does not: it is limited by the CATALOG, so an athlete of ten
 * years has performed a few hundred distinct movements and never will have more.
 *
 * And the interaction is a search. Typing "squ" has to narrow the list as the letters land — a
 * round trip per keystroke on a gym's wifi is the difference between a search box and a search
 * box that feels broken. So the whole set arrives once and the filtering is local.
 * `shared/contracts/movements.ts` carries the same argument from the wire's side.
 */

/** A stable empty list, so a pending query does not hand the filter a new array every render. */
const EMPTY: MovementSummaryModel[] = [];

/**
 * THE ORDER THE GROUPS APPEAR IN, and it is not alphabetical.
 *
 * 🚨 IT IS THE ENGINE'S OWN THREE, IN THE ORDER A SESSION USES THEM: the barbell work an
 * athlete came to do, then the gymnastics, then the engine. Alphabetical would put Gymnastics
 * first for no reason anybody could name, and an order that changes with the app's language —
 * which is what sorting translated headings does — is a list that reorders itself when somebody
 * switches to German.
 *
 * `other` catches a movement whose every recorded set predates the modality field. It is last
 * because it is a residue, and it disappears on its own as an athlete trains.
 */
const GROUP_ORDER = ["weightlifting", "gymnastic", "monostructural", "other"] as const;

/**
 * ═══ 🚨 THE STRENGTH VIEW IS A FILTER ON THIS LIST, NOT A FOURTH TAB (BUD-390) ═══
 *
 * The handoff's frame `8a` draws it as a third segment beside Sessions and Benchmarks. It is
 * not one, for three reasons that outlive this file:
 *
 *   · `HISTORY_TABS` is a three-item segmented row on a 400px phone. A fourth segment takes the
 *     row from comfortable to cramped, and the labels are already words rather than icons.
 *   · Two lists over one library drift. One list with a scope keeps the search box, the
 *     grouping, the truncation notice and the empty states in a single place, which is where
 *     they were already correct.
 *   · This is the THIRD surface printing a number the movement screen and the session screen
 *     already print. It adds reach, not capability, and a surface that adds reach should not
 *     cost a permanent place in the app's navigation.
 *
 * ⚠️ AND IT IS A FILTER, NOT A SORT. The order stays "most recently trained first" — the same
 * order the unfiltered list uses — because the lift an athlete just did is the one they are
 * looking for, and a list that reorders itself when a chip is pressed is a list that has lost
 * the row somebody was reading.
 */


export function MovementList({ onOpen }: { onOpen: (movement: MovementSummaryModel) => void }) {
  const { t, i18n } = useTranslation("movements");
  const library = useMovementLibrary();
  const [query, setQuery] = useState("");
  /* INTERFACE STATE, SO IT IS LOCAL. Which tab of History is showing lives in the URL because
     it is a place in the app you can come back to; a filter inside one tab is not — it is the
     state of a control, and putting it in the URL would mean every movement opened from here
     carried it back out again. */
  /**
   * "3 WEEKS AGO", IN THE READER'S OWN LANGUAGE.
   *
   * ⚠️ COUNTED IN WHOLE DAYS FROM LOCAL MIDNIGHT TO LOCAL MIDNIGHT, never by dividing a
   * difference of instants. A session performed yesterday evening is 14 hours ago by
   * subtraction, which floors to "today" — and an athlete reading that a lift they have not
   * touched since last night is today's work has been told something false by a rounding rule.
   * `civilDate` gives local midnight; both sides are then the same kind of thing.
   */
  const sinceLabel = useMemo(() => {
    const relative = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (day: string) => {
      const days = Math.round((civilDate(day).getTime() - today.getTime()) / 86_400_000);
      if (days > -7) return relative.format(days, "day");
      if (days > -28) return relative.format(Math.round(days / 7), "week");
      if (days > -365) return relative.format(Math.round(days / 30), "month");
      return relative.format(Math.round(days / 365), "year");
    };
  }, [i18n.language]);

  /**
   * HOW MANY WHOLE WEEKS AGO A DAY WAS — the number beside the stale marker.
   *
   * ⚠️ COUNTED FROM LOCAL MIDNIGHT, for the reason `sinceLabel` above gives at length: a
   * difference of instants floors the wrong way and would print "11 wks" on the day the marker
   * appears at twelve.
   */
  const weeksSince = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (day: string) =>
      Math.max(0, Math.floor((today.getTime() - civilDate(day).getTime()) / (7 * 86_400_000)));
  }, []);

  /**
   * A DAY, SHORT ENOUGH TO SIT AT THE END OF A ROW — "28 Aug", and "28 Aug 25" once it is not
   * this year. The same reading and the same shape `MovementDetail` gives a bucket's day, so a
   * date on a row and a date on the screen it opens do not arrive in two different formats.
   */
  const shortDate = useMemo(() => {
    const thisYear = new Date().getFullYear();
    return (day: string) => {
      const at = civilDate(day);
      return at.toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "short",
        ...(at.getFullYear() === thisYear ? {} : { year: "2-digit" }),
      });
    };
  }, [i18n.language]);

  /* ⚠️ MEMOISED EVEN THOUGH IT LOOKS LIKE A ONE-LINER. `?? []` mints a NEW array on every
     render while the query is pending, so the filter below it would re-run on every keystroke's
     re-render and on every unrelated one — and `react-hooks/exhaustive-deps` says so. */
  const items = useMemo(() => library.data?.items ?? EMPTY, [library.data]);
  /**
   * THE SCOPE NARROWS BEFORE THE SEARCH DOES, and the two are separate memos on purpose: typing
   * re-runs only the cheap half, and the scoped set is what the empty states below have to
   * reason about. "No barbell lifts yet" and "nothing matches 'squ'" are different sentences.
   *
   * 🚨 `barbell` IS THE SERVICE'S FIELD AND THE CATALOG'S ANSWER. Not `modality`, which files a
   * KB swing and a thruster under one word, and emphatically not a list of slugs written here —
   * the engine's own catalog opens by refusing exactly that: "NOTHING IN THE ENGINE TARGETS A
   * MOVEMENT BY NAME".
   */
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === "") return items;
    /* THE SLUG IS SEARCHED AS WELL AS THE NAME. An athlete typing "kb" finds "KB swing" through
       its name, and one typing "kettlebell" finds it through `kb-swing` only if the slug says
       so — matching both is free and catches the case where the printed name and the word
       somebody thinks in are different. */
    return items.filter(
      (item) => item.name.toLowerCase().includes(needle) || item.movement.includes(needle),
    );
  }, [items, query]);

  /**
   * ═══ 🚨 A CARD FOR EVERY WEIGHT, AND A ROW FOR EVERYTHING ELSE (BUD-398) ═══
   *
   * THE BARBELL FILTER IS GONE AND IT WAS THE WRONG QUESTION. Settled with the product owner
   * 2026-09-21 — *"i don't want this barbell tab. it doesn't make sense. i want the weight also
   * for dumbells, etc. or what ever we trained"*. A dumbbell press, a kettlebell clean and a bar
   * are all loads somebody chose and can get stronger at, and a strength view that shows only
   * the bar answers "how is your squat" to an athlete whose last eight weeks were dumbbells.
   *
   * 🔑 THE TEST IS WHETHER THERE IS A NUMBER, not what the number was hanging off. A card leads
   * with a figure; a movement with no max has none, so it has nothing to lead with and belongs
   * in the plain list underneath with its "last trained" — which is the only thing anybody can
   * say about a burpee.
   */
  const carded = useMemo(() => filtered.filter((item) => item.max !== null), [filtered]);
  const rest = useMemo(() => filtered.filter((item) => item.max === null), [filtered]);

  if (library.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-touch-lg w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }

  /**
   * ⚠️ EMPTY IS THE NORMAL STATE FOR EVERY EXISTING ATHLETE, and the copy has to say so rather
   * than implying something is broken. Sets are recorded from the day this shipped and there is
   * no back-fill: a movement's history begins the next time it is trained, which is a fact worth
   * stating once rather than leaving somebody to wonder where their year went.
   */
  if (items.length === 0) {
    return (
      <div data-testid="movements-empty">
        <EmptyState
          illustration={<Search className="text-muted-foreground size-8" />}
          title={t("empty.title")}
          description={t("empty.body")}
        />
      </div>
    );
  }

  /**
   * ═══ 🚨 GROUPED, BECAUSE A FLAT ALPHABET OF FORTY NAMES IS NOT A LIST ANYBODY SCANS ═══
   *
   * An athlete looking for their squat is not looking alphabetically; they are looking in the
   * barbell section. The grouping is the engine's own modality, carried on every recorded set
   * rather than derived here — every cheap proxy for it is wrong somewhere, and "has a weight
   * on it" files a weighted pull-up under weightlifting.
   *
   * ⚠️ AN EMPTY GROUP IS NOT DRAWN. While the athlete is typing in the search box most groups
   * have nothing in them, and three empty headings above one result is a screen that looks
   * broken. The order is fixed (see `GROUP_ORDER`) so the list never reshuffles under a thumb.
   */
  /**
   * ⚠️ AND THE BARBELL SCOPE IS ONE GROUP WITH NO HEADING. Every lift loaded by a bar is
   * `weightlifting`, so the grouping collapses to a single section — and a sticky "WEIGHTLIFTING"
   * heading over a list that cannot contain anything else is ink that says nothing. The scope
   * chip above it has already said it.
   */
  /**
   * THE PLAIN LIST IS GROUPED BY MODALITY and the cards are not, which is deliberate: a card
   * carries a number an athlete compares ACROSS groups — a dumbbell press against a bar press —
   * and three headings between them would be three reasons not to.
   *
   * ⚠️ AN EMPTY GROUP IS NOT DRAWN. While the athlete is typing most groups have nothing in
   * them, and three empty headings above one result is a screen that looks broken. The order is
   * fixed (see `GROUP_ORDER`) so the list never reshuffles under a thumb.
   */
  const groups = GROUP_ORDER.map((group) => ({
    group,
    items: rest.filter((item) => (item.modality ?? "other") === group),
  })).filter((entry) => entry.items.length > 0);

  return (
    <div className="flex flex-col gap-3">
      <label className="bg-card rounded-field flex items-center gap-2 px-3">
        <Search className="text-muted-foreground size-4 shrink-0" />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("search")}
          aria-label={t("search")}
          data-testid="movement-search"
          /* 🚨 16px MINIMUM ON ANY INPUT. Below it iOS zooms the page on focus and never zooms
             back out, leaving the app scrolled sideways with its header off screen. `text-row`
             is the type role that clears it. */
          className="text-row h-touch-lg min-w-0 flex-1 bg-transparent outline-none"
        />
      </label>

      {filtered.length === 0 ? (
        /* TWO DIFFERENT NOTHINGS, AND THEY NEED TWO DIFFERENT SENTENCES. An athlete who has
           never trained anything has not mistyped, and `Nothing matches ""` — which is what the
           search message renders with an empty box — reads as a bug. */
        items.length === 0 ? (
          <p
            data-testid="movements-none"
            className="text-support text-muted-foreground py-6 text-center"
          >
            {t("empty.body")}
          </p>
        ) : (
          <p
            data-testid="movements-no-match"
            className="text-support text-muted-foreground py-6 text-center"
          >
            {t("noMatch", { query: query.trim() })}
          </p>
        )
      ) : null}

      {carded.length === 0 ? null : (
        <ul data-testid="movement-cards" className="flex list-none flex-col gap-2">
          {carded.map((item) => (
            <li key={item.movement}>
              <StrengthCard
                item={item}
                onOpen={onOpen}
                shortDate={shortDate}
                weeksSince={weeksSince}
              />
            </li>
          ))}
        </ul>
      )}

      {groups.length === 0 ? null : (
        <>
          {/* 🚨 WHERE EVERYTHING WITHOUT A NUMBER WENT, said once and above them rather than left
              to be discovered by scrolling. The handoff's frame `8a` puts a line exactly here for
              exactly this reason — a screen that silently drops two thirds of what somebody has
              trained reads as a screen that has lost it. */}
          <p data-testid="movements-rest-note" className="text-legal text-foreground-faint pt-2">
            {t("restNote")}
          </p>
          {groups.map(({ group, items: rows }) => (
            <section key={group} className="flex flex-col">
              {/* THE HEADING IS STICKY, so scrolling a long section never leaves an athlete
                  looking at a list of names with no idea which group they are in. */}
              <h3
                data-testid="movement-group"
                data-movement-group={group}
                className="text-eyebrow text-muted-foreground bg-background sticky top-0 z-10 py-1.5 uppercase"
              >
                {t(`group.${group}`)}
              </h3>
              <ul data-testid="movement-list" className="flex flex-col">
                {rows.map((item) => (
                  <li key={item.movement}>
                    <button
                      type="button"
                      data-testid="movement-row"
                      onClick={() => {
                        haptics.tap();
                        onOpen(item);
                      }}
                      /*
                        NO DIVIDER, NO CHEVRON, NO SECOND LINE — the three things that were making
                        forty rows read as a table. Whitespace and the type scale carry the
                        structure instead; a hairline between every pair of rows is ink that says
                        nothing the spacing has not already said.
                      */
                      className="flex min-h-touch w-full items-baseline gap-3 py-2 text-left"
                    >
                      <span className="text-row min-w-0 flex-1 truncate">{item.name}</span>
                      <span
                        data-testid="movement-row-value"
                        /* 🔑 THE VALUE IS QUIETER THAN THE NAME. The name is what an athlete is
                           scanning FOR; the number is what they read once they have found it. */
                        className="text-support text-muted-foreground shrink-0 tabular-nums"
                      >
                        {sinceLabel(item.lastPerformedOn)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      )}

      {/* THE CEILING WAS REACHED — say so rather than quietly showing a subset. In practice this
          means a bug rather than a prolific athlete, and it should be visible either way. */}
      {library.data?.truncated === true ? (
        <p className="text-eyebrow text-muted-foreground text-center">{t("truncated")}</p>
      ) : null}
    </div>
  );
}

/**
 * ═══ 🚨 ONE LIFT, AS A CARD — frame `8a` (BUD-398) ═══
 *
 * A ROW COULD SAY WHAT THE NUMBER IS. ONLY A CARD CAN SAY WHERE IT IS GOING. The list this
 * replaced printed `Back squat … 127 kg` and nothing else, which answers "what can I lift" and
 * cannot answer "is this going anywhere" — and the second question is the one somebody scanning
 * their own lifts is actually asking. So the card carries four things, in the order they are
 * read: the name, the number, the LINE behind it, and how far it has moved since New Year.
 *
 * ⚠️ THE LINE IS DRAWN ONLY WHERE THERE IS ONE TO DRAW. Two points is the floor `canDrawSparkline`
 * sets — one dot floating in a 28px box reads as a chart that failed to load to somebody with
 * four seconds to look at it — so a lift with one month of history simply has no line, and the
 * card closes up around it.
 *
 * ⚠️ AND THE YEAR FIGURE IS THE SERVICE'S ARITHMETIC, NOT A SUBTRACTION DONE HERE. Both of its
 * ends are estimates off logged sets; the headline above it may be a number somebody typed. See
 * `yearChangeKg` in the contract for why mixing the two prints a year of progress that is really
 * a change of claim.
 */
function StrengthCard({
  item,
  onOpen,
  shortDate,
  weeksSince,
}: {
  item: MovementSummaryModel;
  onOpen: (item: MovementSummaryModel) => void;
  shortDate: (day: string) => string;
  weeksSince: (day: string) => number;
}) {
  const { t } = useTranslation("movements");
  const units = useUnits();
  const max = item.max;
  if (max === null) return null;

  const series = standingSeries(item.series);

  return (
    <button
      type="button"
      data-testid="movement-card"
      data-movement={item.movement}
      onClick={() => {
        haptics.tap();
        onOpen(item);
      }}
      className="rounded-card bg-card px-gutter flex min-h-touch w-full flex-col gap-1 py-3 text-left"
    >
      <span className="flex w-full items-baseline gap-2">
        <span className="text-row min-w-0 flex-1 truncate">{item.name}</span>
        <span
          data-testid="movement-card-value"
          /* 🔑 THE NUMBER IS THE POINT OF THE CARD and it is the only thing on it set above row
             size. `printedMax` decides the precision from the source, so a card saying 159 and
             the screen it opens cannot differ by a rounding. */
          className="text-title shrink-0 tabular-nums"
        >
          {printedMax(max, units.fromKilos)}{" "}
          <span className="text-support text-muted-foreground">{units.unit}</span>
        </span>
        <Badge data-testid="movement-card-source" variant="neutral" className="shrink-0">
          {t(`source.${max.source}`)}
        </Badge>
      </span>

      {canDrawSparkline(series) ? (
        <MovementSparkline
          series={series}
          /* `aspect-auto` CANCELS `ChartContainer`'S OWN `aspect-video`. At full size the 16:9
             default is what gives a chart its box; here the card decides the height and the
             width is the card's, and a ratio fighting both is a thumbnail that grows the card
             it sits in. */
          className="aspect-auto h-8 w-full"
        />
      ) : null}

      <ProvenanceLine
        item={item}
        shortDate={shortDate}
        weeksSince={weeksSince}
        yearChangeKg={item.yearChangeKg}
      />
    </button>
  );
}

/**
 * ═══ 🚨 THE SECOND LINE OF A STRENGTH ROW — WHERE THE NUMBER CAME FROM, AND WHETHER IT IS STILL
 * TRUE (BUD-390) ═══
 *
 * The badge on the line above says HOW the number was arrived at. This says WHEN, and — for the
 * one source that has a set behind it — WHICH SET. Splitting it that way is what keeps the row
 * from saying "Tested" twice: the handoff's frame `8a` draws the chip `REC` and the line
 * `tested 12 Jun` together, and on a real 400px row those are the same word printed twice.
 *
 * ═══ 🚨 THE STALE MARKER IS DATED, NEVER REDUCED ═══
 *
 * It sits BESIDE the date and changes nothing about the figure above it. `maxStale` is the
 * service's carry of the engine's own ageing predicate over the winning fact, against the
 * engine's own `maxRetestDays` dial — twelve weeks, one strength cycle — so there is no second
 * threshold anywhere in this app. `api/src/engine/capability/facts.ts` states the doctrine this
 * is the screen half of: "a stale fact is not an unknown fact… keeps its value, loses certainty
 * toward a floor it never reaches, and emits an intent to measure it again". Dimming or
 * discounting the number here would put the decay back on the glass that the arithmetic refuses
 * to do — and an athlete whose deadlift drops thirty kilos because they went on holiday has been
 * told something false by a rendering rule.
 *
 * ⚠️ NOTHING IS DRAWN WHERE NOTHING IS KNOWN. A `derived` max came through the ratio chain from
 * a different lift and a `assumed` one from the population model; neither has a set, and an
 * undated one has no age either. The badge above has already disclosed both, and inventing a
 * date under them would be the one failure this row cannot afford.
 */
function ProvenanceLine({
  item,
  shortDate,
  weeksSince,
  yearChangeKg,
}: {
  item: MovementSummaryModel;
  shortDate: (day: string) => string;
  weeksSince: (day: string) => number;
  /** How far the lift has moved since New Year, or null where there is nothing to compare to. */
  yearChangeKg: number | null;
}) {
  const { t, i18n } = useTranslation("movements");
  const units = useUnits();
  const max = item.max;

  /* NO MAX MEANS THE LINE ABOVE IS ALREADY ANSWERING "WHEN", so this one has nothing to add. */
  if (max === null) return null;

  const when = max.observedOn === null ? null : shortDate(max.observedOn);
  /* THE SET AS IT WAS PERFORMED — a measured load, so it keeps its half-kilo, which is
     `groupedWeight`'s whole job and the same reading the movement screen gives a best set. It is
     never re-derived into the max: `105 kg × 5` is a fact and `121` is what the engine made of
     it. `bestSetLoaded` is reused rather than copied: one phrase, one translation, one place a
     language that writes the multiplication sign differently has to be fixed. */
  const set =
    max.fromSet === null
      ? null
      : t("bestSetLoaded", {
          weight: `${groupedWeight(units.fromKilos(max.fromSet.loadKg))} ${units.unit}`,
          reps: max.fromSet.reps,
        });

  /* JOINED RATHER THAN INTERPOLATED, so an undated estimate does not print a dangling separator
     with nothing after it. */
  const parts = [set, when].filter((part): part is string => part !== null);
  const provenance = parts.length === 0 ? null : parts.join(" · ");

  /* 🚨 SIGNED, ALWAYS, AND `+` IS NOT DECORATION. Unsigned, `6 kg this year` reads as a total
     rather than a change, and the negative case — a year whose best set was lighter than last
     year's — needs its own sign to be legible beside it. `Intl` writes the sign in the reader's
     own convention rather than this file gluing a "+" on the front of a localised number. */
  const year =
    yearChangeKg === null
      ? null
      : t("yearChange", {
          delta: `${new Intl.NumberFormat(i18n.language, {
            signDisplay: "exceptZero",
            maximumFractionDigits: 1,
          }).format(units.fromKilos(yearChangeKg))} ${units.unit}`,
        });

  if (provenance === null && year === null && !max.stale) return null;

  return (
    <span className="text-support text-muted-foreground flex w-full items-baseline gap-2">
      <span data-testid="movement-row-provenance" className="min-w-0 flex-1 truncate tabular-nums">
        {provenance}
      </span>
      {year === null ? null : (
        /* `gain` IS TEXT-ONLY BY RULE — a gain is never a fill (design system, sheet 3.6) — and
           the same treatment is used whichever way the year went, because a year that went
           backwards is information rather than a warning. */
        <span data-testid="movement-row-year" className="text-gain shrink-0 tabular-nums">
          {year}
        </span>
      )}
      {max.stale && max.observedOn !== null ? (
        <Badge data-testid="movement-row-stale" variant="neutral">
          {t("stale", { weeks: weeksSince(max.observedOn) })}
        </Badge>
      ) : null}
    </span>
  );
}
